import React, { useState } from 'react';
import {
  ShieldAlert,
  Check,
  X,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

export default function ApprovalGate({
  action_id = '',
  thread_id = '',
  tool = '',
  arguments: toolArgs = {},
  reason = '',
  status: initialStatus = 'pending',
  onResolve
}) {
  const [status, setStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(false);

  const handleDecision = async (decision) => {
    setLoading(true);
    try {
      if (onResolve) {
        await onResolve(action_id, decision);
      } else {
        // Fallback: send decision to backend resolve_approval endpoint if available
        await fetch('/api/method/dashnode_ai.api.resolve_approval', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action_id, decision, thread_id })
        }).catch(() => {});
      }
      setStatus(decision === 'approve' ? 'approved' : 'rejected');
    } finally {
      setLoading(false);
    }
  };

  const isPending = status === 'pending';
  const isApproved = status === 'approved' || status === 'done';
  const isRejected = status === 'rejected';

  return (
    <div
      className={`border-2 rounded-xl p-4 my-3 transition-all animate-in fade-in duration-200 ${
        isPending
          ? 'border-amber-400 bg-amber-50/70 text-amber-950 shadow-xs'
          : isApproved
          ? 'border-emerald-400 bg-emerald-50/70 text-emerald-950'
          : 'border-slate-300 bg-slate-50/70 text-slate-700'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* State Icon */}
        <div className="shrink-0 mt-0.5">
          {isPending && <ShieldAlert className="w-5 h-5 text-amber-600 animate-pulse" />}
          {isApproved && <CheckCircle className="w-5 h-5 text-emerald-600" />}
          {isRejected && <XCircle className="w-5 h-5 text-slate-500" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 text-xs">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="font-bold text-xs uppercase tracking-tight">
                {isPending ? 'Human Approval Required' : `Action ${status.toUpperCase()}`}
              </span>
              {tool && (
                <code className="px-1.5 py-0.5 rounded bg-black/10 font-mono text-[11px] font-bold">
                  {tool}
                </code>
              )}
            </div>
            {action_id && (
              <span className="font-mono text-[10px] opacity-75 font-semibold">
                Action ID: {action_id}
              </span>
            )}
          </div>

          <p className="mt-1.5 text-xs text-slate-700 leading-relaxed font-normal">
            {reason}
          </p>

          {toolArgs && Object.keys(toolArgs).length > 0 && (
            <div className="mt-2 text-[10px] font-mono bg-black/5 rounded p-1.5 text-slate-600 truncate">
              Params: {JSON.stringify(toolArgs)}
            </div>
          )}

          {/* Decision Buttons */}
          {isPending ? (
            <div className="mt-3 flex items-center gap-2 pt-1 border-t border-amber-200/60">
              <button
                type="button"
                disabled={loading}
                onClick={() => handleDecision('approve')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-medium text-xs shadow-xs transition-all active:scale-[0.98] cursor-pointer"
              >
                {loading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                )}
                <span>Approve & Execute</span>
              </button>

              <button
                type="button"
                disabled={loading}
                onClick={() => handleDecision('reject')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 rounded-lg font-medium text-xs transition-colors cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
                <span>Reject</span>
              </button>
            </div>
          ) : (
            <div className="mt-2 pt-2 border-t border-black/5 flex items-center gap-1.5 text-[11px] font-semibold">
              {isApproved && (
                <span className="text-emerald-700 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Action approved and executed by engineering operator.
                </span>
              )}
              {isRejected && (
                <span className="text-slate-600 flex items-center gap-1">
                  <X className="w-3.5 h-3.5" /> Action was declined. Original feature topology preserved.
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
